import React from 'react';
import { Form, Button } from 'react-bootstrap';
import { FaPlus, FaMinus } from 'react-icons/fa';
import RegistrationNumberForm from '../RegistrationNumberForm';
import { InputFieldState } from '../types';
import options from './options.json';

/**
 * RegistrationNumbersFormコンポーネント用のPropsです。
 * @interface RegistrationNumbersFormProps
 */
interface RegistrationNumbersFormProps {

  /**
   * 法域
   * @type {InputFieldState}
   * @memberof RegistrationNumberFormProps
   */
  jurisdiction: InputFieldState;

  /**
   * 法域を設定する関数
   * @memberof RegistrationNumberFormProps
   */
  setJurisdiction: (jurisdiction: InputFieldState) => void;

  /**
   * 特許（登録）番号
   * @type {InputFieldState}
   * @memberof RegistrationNumberFormProps
   */
  registrationNumbers: InputFieldState[];

  /**
   * 特許（登録）番号を設定する関数
   * @memberof RegistrationNumberFormProps
   */
  setRegistrationNumbers: (registrationNumbers: InputFieldState[]) => void;

}

/**
 * 登録番号フォームのコンポーネントです。
 * @param {string} props.otherAppointments - 法域
 * @param {function} props.onChange - 法域が変更されたときに呼ばれるコールバック関数
 * @param {string} props.otherAppointments - 登録番号
 * @param {function} props.onChange - 登録番号が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 登録番号フォームのJSX要素
 */
const RegistrationNumbersForm: React.FC<RegistrationNumbersFormProps> = ({ jurisdiction, setJurisdiction, registrationNumbers, setRegistrationNumbers }) => {

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJurisdiction({
      value: event.target.value,
      errorMessage: ''
    });
  };

  /**
   * 登録番号フォームを追加する関数です。
   * @param {string} addedRegistrationNumber - 登録番号
   * @returns {void}
   */
  const addRegistrationNumber = (addedRegistrationNumber: string): void => {
    setRegistrationNumbers([...registrationNumbers, { value: addedRegistrationNumber, errorMessage: ''}]);
  }

  /**
   * 登録番号フォームを削除する関数です。
   * @param {number} removedRegistrationNumberIndex - 削除する登録番号の索引
   * @returns {void}
   */
  const deleteRegistrationNumber = (removedRegistrationNumberIndex: number): void => {
    if(removedRegistrationNumberIndex !== 0) {
      setRegistrationNumbers(
        registrationNumbers.filter((_registrationNumber, i) => (i !== removedRegistrationNumberIndex))
      )
    }
  }

  /**
   * 登録番号フォームを更新する関数です。
   * @param {number} updatedRegistrationNumberIndex - 更新する登録番号の索引
   * @param {string} updatedRegistrationNumber - 更新する登録番号
   * @returns {void}
   */
  const updateRegistrationNumber = (updatedRegistrationNumberIndex: number, updatedRegistrationNumber: string): void => {

    const duplicatedRegistrationNumberIndexes: number[] = [];
    registrationNumbers.forEach((item, index) => {
      if(updatedRegistrationNumberIndex !== index && item.value === updatedRegistrationNumber) {
        duplicatedRegistrationNumberIndexes.push(index);
      }
    });

    const regexp = /\d{7}/;
    let errorMessage = '';
    if(updatedRegistrationNumber === '') {
      errorMessage = '特許（登録）番号が入力されていません。';
    } else if(!regexp.test(updatedRegistrationNumber)) {
      errorMessage = '入力された特許（登録）番号の形式（7桁の数字）が正しくありません。';
    } else if(duplicatedRegistrationNumberIndexes.length !== 0) {
      errorMessage = '重複して特許（登録）番号が入力されています。';
    }

    setRegistrationNumbers(
      registrationNumbers.map((registrationNumber, i) => (i === updatedRegistrationNumberIndex ? 
        {
          value: updatedRegistrationNumber,
          errorMessage: errorMessage
        }
        :
        {
          value: registrationNumber.value,
          errorMessage: errorMessage !== '' && duplicatedRegistrationNumberIndexes.includes(i) ? errorMessage : ''
        }
      ))
    )
  }

  return (
    <Form.Group className="mb-3 row">
      <Form.Label className="col-lg-2 col-12">
        登録番号
        <span className="text-small text-danger ms-3">※必須</span>
      </Form.Label>
      <div className="col-lg-10 col-12 ps-lg-5">
        {
          options.map((option, index) => {
            return (
              <Form.Check
                key={index}
                type="radio"
                className="form-check-inline"
                value={option.value}
                label={option.label}
                id={`jurisdictionType${index}`}
                checked={jurisdiction.value === option.value}
                onChange={handleChange}
              />
            );
          })
        }
        <div className="text-end" style={{marginTop: '-2rem'}}>
          <div className="mx-1 d-inline">
            <Button
              variant='primary'
              className="rounded-circle py-1 px-2 btn-sm text-white"
              onClick={() => {addRegistrationNumber('');}}
              >
              <FaPlus />
            </Button>
          </div>
          <div className="mx-1 d-inline">
            <Button
              variant="danger"
              className="rounded-circle py-1 px-2 btn-sm text-white" 
              onClick={() => {deleteRegistrationNumber(registrationNumbers.length-1);}}
              disabled={registrationNumbers.length === 1 ? true : false}
            >
              <FaMinus />
            </Button>
          </div>
        </div>
        {registrationNumbers.map((registrationNumber, index) => {
          return (
            <div key={index} className="my-2">
              <RegistrationNumberForm
                index={index}
                registrationNumber={registrationNumber}
                onChange={updateRegistrationNumber}
              />
            </div>
          );
        })}
      </div>
    </Form.Group>
  );
}

export default RegistrationNumbersForm;